export type EtfRegion = 'US' | 'Canada';
export type EtfCategory = 'Broad Market' | 'International' | 'Sector' | 'Bonds';

export interface Etf {
  ticker: string;
  name: string;
  category: EtfCategory;
  region: EtfRegion;
  expenseRatio: number;
  whatItTracks: string;
  samplePrice: number;
  sampleDate: string;
  currency: 'USD' | 'CAD';
}

const SAMPLE_DATE = '2026-04-20';

export const SHORTLIST_ETFS: Etf[] = [
  {
    ticker: 'VOO',
    name: 'Vanguard S&P 500 ETF',
    category: 'Broad Market',
    region: 'US',
    expenseRatio: 0.03,
    whatItTracks: 'The 500 largest publicly traded US companies (S&P 500 index).',
    samplePrice: 542.18,
    sampleDate: SAMPLE_DATE,
    currency: 'USD',
  },
  {
    ticker: 'VTI',
    name: 'Vanguard Total Stock Market ETF',
    category: 'Broad Market',
    region: 'US',
    expenseRatio: 0.03,
    whatItTracks: 'Virtually every publicly traded US stock — large, mid, and small cap.',
    samplePrice: 298.45,
    sampleDate: SAMPLE_DATE,
    currency: 'USD',
  },
  {
    ticker: 'VXUS',
    name: 'Vanguard Total International Stock ETF',
    category: 'International',
    region: 'US',
    expenseRatio: 0.07,
    whatItTracks: 'Stocks from developed and emerging markets outside the US.',
    samplePrice: 68.92,
    sampleDate: SAMPLE_DATE,
    currency: 'USD',
  },
  {
    ticker: 'BND',
    name: 'Vanguard Total Bond Market ETF',
    category: 'Bonds',
    region: 'US',
    expenseRatio: 0.03,
    whatItTracks: 'Broad US investment-grade bond market (government, corporate, and mortgage-backed).',
    samplePrice: 72.64,
    sampleDate: SAMPLE_DATE,
    currency: 'USD',
  },
  {
    ticker: 'XIC',
    name: 'iShares Core S&P/TSX Capped Composite Index ETF',
    category: 'Broad Market',
    region: 'Canada',
    expenseRatio: 0.06,
    whatItTracks: 'The broad Canadian equity market (S&P/TSX Capped Composite Index).',
    samplePrice: 38.12,
    sampleDate: SAMPLE_DATE,
    currency: 'CAD',
  },
  {
    ticker: 'XAW',
    name: 'iShares Core MSCI All Country World ex Canada Index ETF',
    category: 'International',
    region: 'Canada',
    expenseRatio: 0.22,
    whatItTracks: 'Global equities — developed and emerging markets — excluding Canada.',
    samplePrice: 42.77,
    sampleDate: SAMPLE_DATE,
    currency: 'CAD',
  },
  {
    ticker: 'ZAG',
    name: 'BMO Aggregate Bond Index ETF',
    category: 'Bonds',
    region: 'Canada',
    expenseRatio: 0.09,
    whatItTracks: 'A broad basket of Canadian investment-grade government and corporate bonds.',
    samplePrice: 14.28,
    sampleDate: SAMPLE_DATE,
    currency: 'CAD',
  },
  {
    ticker: 'VCN',
    name: 'Vanguard FTSE Canada All Cap Index ETF',
    category: 'Broad Market',
    region: 'Canada',
    expenseRatio: 0.05,
    whatItTracks: 'Canadian stocks across large, mid, and small cap segments.',
    samplePrice: 51.34,
    sampleDate: SAMPLE_DATE,
    currency: 'CAD',
  },
];

export const EXTENDED_ETFS: Etf[] = [
  ...SHORTLIST_ETFS,
  {
    ticker: 'SPY',
    name: 'SPDR S&P 500 ETF Trust',
    category: 'Broad Market',
    region: 'US',
    expenseRatio: 0.095,
    whatItTracks: 'The S&P 500 — oldest and most heavily traded US equity ETF.',
    samplePrice: 589.42,
    sampleDate: SAMPLE_DATE,
    currency: 'USD',
  },
  {
    ticker: 'IVV',
    name: 'iShares Core S&P 500 ETF',
    category: 'Broad Market',
    region: 'US',
    expenseRatio: 0.03,
    whatItTracks: 'The S&P 500 index — a low-cost alternative to SPY.',
    samplePrice: 592.11,
    sampleDate: SAMPLE_DATE,
    currency: 'USD',
  },
  {
    ticker: 'QQQ',
    name: 'Invesco QQQ Trust',
    category: 'Sector',
    region: 'US',
    expenseRatio: 0.2,
    whatItTracks: 'The Nasdaq-100 — 100 largest non-financial Nasdaq companies, tech-heavy.',
    samplePrice: 498.27,
    sampleDate: SAMPLE_DATE,
    currency: 'USD',
  },
  {
    ticker: 'VEA',
    name: 'Vanguard FTSE Developed Markets ETF',
    category: 'International',
    region: 'US',
    expenseRatio: 0.06,
    whatItTracks: 'Stocks from developed economies outside the US (Europe, Japan, etc.).',
    samplePrice: 54.18,
    sampleDate: SAMPLE_DATE,
    currency: 'USD',
  },
  {
    ticker: 'VWO',
    name: 'Vanguard FTSE Emerging Markets ETF',
    category: 'International',
    region: 'US',
    expenseRatio: 0.08,
    whatItTracks: 'Stocks from emerging markets like China, India, and Brazil.',
    samplePrice: 46.72,
    sampleDate: SAMPLE_DATE,
    currency: 'USD',
  },
  {
    ticker: 'SCHD',
    name: 'Schwab US Dividend Equity ETF',
    category: 'Sector',
    region: 'US',
    expenseRatio: 0.06,
    whatItTracks: 'US companies with strong, sustainable dividend track records.',
    samplePrice: 28.94,
    sampleDate: SAMPLE_DATE,
    currency: 'USD',
  },
  {
    ticker: 'VNQ',
    name: 'Vanguard Real Estate ETF',
    category: 'Sector',
    region: 'US',
    expenseRatio: 0.13,
    whatItTracks: 'US real estate investment trusts (REITs) — commercial and residential.',
    samplePrice: 91.15,
    sampleDate: SAMPLE_DATE,
    currency: 'USD',
  },
  {
    ticker: 'XLK',
    name: 'Technology Select Sector SPDR Fund',
    category: 'Sector',
    region: 'US',
    expenseRatio: 0.09,
    whatItTracks: 'Technology companies in the S&P 500.',
    samplePrice: 237.48,
    sampleDate: SAMPLE_DATE,
    currency: 'USD',
  },
  {
    ticker: 'XLV',
    name: 'Health Care Select Sector SPDR Fund',
    category: 'Sector',
    region: 'US',
    expenseRatio: 0.09,
    whatItTracks: 'Healthcare companies in the S&P 500.',
    samplePrice: 148.63,
    sampleDate: SAMPLE_DATE,
    currency: 'USD',
  },
  {
    ticker: 'AGG',
    name: 'iShares Core US Aggregate Bond ETF',
    category: 'Bonds',
    region: 'US',
    expenseRatio: 0.03,
    whatItTracks: 'The broad US investment-grade bond market.',
    samplePrice: 98.27,
    sampleDate: SAMPLE_DATE,
    currency: 'USD',
  },
  {
    ticker: 'TLT',
    name: 'iShares 20+ Year Treasury Bond ETF',
    category: 'Bonds',
    region: 'US',
    expenseRatio: 0.15,
    whatItTracks: 'Long-duration US Treasury bonds (20+ years maturity).',
    samplePrice: 89.54,
    sampleDate: SAMPLE_DATE,
    currency: 'USD',
  },
  {
    ticker: 'SHY',
    name: 'iShares 1-3 Year Treasury Bond ETF',
    category: 'Bonds',
    region: 'US',
    expenseRatio: 0.15,
    whatItTracks: 'Short-duration US Treasury bonds (1–3 years) — lowest volatility.',
    samplePrice: 82.14,
    sampleDate: SAMPLE_DATE,
    currency: 'USD',
  },
  {
    ticker: 'VFV',
    name: 'Vanguard S&P 500 Index ETF (CAD)',
    category: 'Broad Market',
    region: 'Canada',
    expenseRatio: 0.09,
    whatItTracks: 'The S&P 500, accessible in Canadian dollars.',
    samplePrice: 148.32,
    sampleDate: SAMPLE_DATE,
    currency: 'CAD',
  },
  {
    ticker: 'VEQT',
    name: 'Vanguard All-Equity ETF Portfolio',
    category: 'Broad Market',
    region: 'Canada',
    expenseRatio: 0.24,
    whatItTracks: '100% global equities — one-ticker diversified stock portfolio.',
    samplePrice: 42.18,
    sampleDate: SAMPLE_DATE,
    currency: 'CAD',
  },
  {
    ticker: 'VGRO',
    name: 'Vanguard Growth ETF Portfolio',
    category: 'Broad Market',
    region: 'Canada',
    expenseRatio: 0.24,
    whatItTracks: '80% equities / 20% bonds — balanced one-ticker growth portfolio.',
    samplePrice: 36.42,
    sampleDate: SAMPLE_DATE,
    currency: 'CAD',
  },
  {
    ticker: 'XEQT',
    name: 'iShares Core Equity ETF Portfolio',
    category: 'Broad Market',
    region: 'Canada',
    expenseRatio: 0.2,
    whatItTracks: '100% global equities — one-ticker diversified stock portfolio.',
    samplePrice: 37.94,
    sampleDate: SAMPLE_DATE,
    currency: 'CAD',
  },
  {
    ticker: 'XUU',
    name: 'iShares Core S&P US Total Market Index ETF',
    category: 'International',
    region: 'Canada',
    expenseRatio: 0.07,
    whatItTracks: 'Broad US equity market, accessible in Canadian dollars.',
    samplePrice: 58.71,
    sampleDate: SAMPLE_DATE,
    currency: 'CAD',
  },
  {
    ticker: 'XEF',
    name: 'iShares Core MSCI EAFE IMI Index ETF',
    category: 'International',
    region: 'Canada',
    expenseRatio: 0.22,
    whatItTracks: 'Developed market stocks outside North America.',
    samplePrice: 34.12,
    sampleDate: SAMPLE_DATE,
    currency: 'CAD',
  },
  {
    ticker: 'VE',
    name: 'Vanguard FTSE Developed Europe All Cap Index ETF',
    category: 'International',
    region: 'Canada',
    expenseRatio: 0.23,
    whatItTracks: 'European stocks across developed markets (UK, Germany, France, Switzerland, etc.).',
    samplePrice: 36.42,
    sampleDate: SAMPLE_DATE,
    currency: 'CAD',
  },
  {
    ticker: 'VA',
    name: 'Vanguard FTSE Developed Asia Pacific All Cap Index ETF',
    category: 'International',
    region: 'Canada',
    expenseRatio: 0.23,
    whatItTracks: 'Developed Asia Pacific stocks (Japan, Australia, Hong Kong, Singapore).',
    samplePrice: 33.18,
    sampleDate: SAMPLE_DATE,
    currency: 'CAD',
  },
  {
    ticker: 'VDU',
    name: 'Vanguard FTSE Developed All Cap ex North America Index ETF',
    category: 'International',
    region: 'Canada',
    expenseRatio: 0.23,
    whatItTracks: 'All developed-market stocks outside North America in one ticker.',
    samplePrice: 41.65,
    sampleDate: SAMPLE_DATE,
    currency: 'CAD',
  },
  {
    ticker: 'VEE',
    name: 'Vanguard FTSE Emerging Markets All Cap Index ETF',
    category: 'International',
    region: 'Canada',
    expenseRatio: 0.24,
    whatItTracks: 'Emerging market stocks (China, India, Taiwan, Brazil, and more).',
    samplePrice: 34.85,
    sampleDate: SAMPLE_DATE,
    currency: 'CAD',
  },
  {
    ticker: 'XEC',
    name: 'iShares Core MSCI Emerging Markets IMI Index ETF',
    category: 'International',
    region: 'Canada',
    expenseRatio: 0.26,
    whatItTracks: 'Broad emerging market equities — large, mid, and small cap across 20+ countries.',
    samplePrice: 29.47,
    sampleDate: SAMPLE_DATE,
    currency: 'CAD',
  },
  {
    ticker: 'XBB',
    name: 'iShares Core Canadian Universe Bond Index ETF',
    category: 'Bonds',
    region: 'Canada',
    expenseRatio: 0.1,
    whatItTracks: 'Broad Canadian investment-grade bonds.',
    samplePrice: 27.84,
    sampleDate: SAMPLE_DATE,
    currency: 'CAD',
  },
  {
    ticker: 'VAB',
    name: 'Vanguard Canadian Aggregate Bond Index ETF',
    category: 'Bonds',
    region: 'Canada',
    expenseRatio: 0.09,
    whatItTracks: 'Broad Canadian investment-grade government and corporate bonds.',
    samplePrice: 23.47,
    sampleDate: SAMPLE_DATE,
    currency: 'CAD',
  },
];
